
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createRole, updateRole } from '../../../../../modules/masterdata/actions';
import { IRole } from '../../../../../modules/masterdata/types';
import { selectCreateRoleSuccess, selectUpdateRoleSuccess } from '../../../../../modules/masterdata/selectors';

interface RoleModalProps {
  open: boolean;
  onClose: () => void;
  role?: IRole | null; // If provided, we are in Edit mode
}

const RoleModal: React.FC<RoleModalProps> = ({ open, onClose, role }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isCreateSuccess = useSelector(selectCreateRoleSuccess);
  const isUpdateSuccess = useSelector(selectUpdateRoleSuccess);

  useEffect(() => {
    if (role) {
      setName(role.role_name);
      setDescription(role.role_description);
    } else {
      setName('');
      setDescription('');
    }
  }, [role, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (role) {
      dispatch(updateRole({
        id: role.role_id,
        role_name: name,
        role_description: description
      }));
    } else {
      dispatch(createRole({
        role_name: name,
        role_description: description
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {role ? 'Edit Role' : 'Create Role'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {role ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default RoleModal;
